import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import type { DataTableSource, Export as ExportFormat } from 'generated/resources/datatable.graphql'

import { EXPORT_FORMATS } from 'modules/core/components/DataTable/constants'
import useExport from 'modules/core/components/DataTable/hooks/useExport'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { FileName } from './types'

type Props<T, SourceFields extends string, ExportFileName extends string> = {
  fileName: FileName<ExportFileName>
  source: DataTableSource<T, SourceFields>
  loading: boolean
  onClose: () => void
  onExportCallback?: () => void
}

function ExportModal<T, SourceFields extends string, ExportFileName extends string>({
  source,
  fileName: fileNameProp,
  loading,
  onClose,
  onExportCallback,
}: Props<T, SourceFields, ExportFileName>) {
  const { onExport, isExporting, fileName, fileFormat, setFileName, setFileFormat } = useExport(
    source,
    fileNameProp
  )

  const onClickExport = () => {
    onExport(onClose)
    onExportCallback?.()
  }

  const options = [
    {
      label: i18n.t('data_table.export.modal.file_format_xlsx'),
      value: EXPORT_FORMATS.EXCEL,
    },
    {
      label: i18n.t('data_table.export.modal.file_format_csv'),
      value: EXPORT_FORMATS.CSV,
    },
  ]

  const disabled = isExporting || loading

  return (
    <Modal
      title={i18n.t('data_table.export.modal.title')}
      description={i18n.t('data_table.export.modal.description')}
      illustration="table-file"
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" onClick={onClickExport} disabled={disabled}>
            {i18n.t('common.export')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <TextField
          label={i18n.t('data_table.export.modal.file_name')}
          value={fileName}
          onChange={setFileName}
          readonly={disabled}
        />
        <SelectField
          label={i18n.t('data_table.export.modal.file_format')}
          value={fileFormat}
          onChange={(value) => setFileFormat(value as Exclude<ExportFormat, false>)}
          options={options}
          readonly={disabled}
        />
      </Box>
    </Modal>
  )
}

export default ExportModal
